import useApi from '../../hooks/useApi'
import { acceptRejectInvitationCompany } from '../../redux/actions/companyReceivedInvitationsActions'
import {
	resendInvitationCompany,
	revokeInvitationCompany,
} from '../../redux/actions/companySentInvitationsActions'
import { resendInvitation, revokeInvitation } from '../../redux/actions/invitationsActions'
import { useAppDispatch } from '../../redux/hooks/reduxHooks'
import { Invitation } from '../../types/Invitation'
import { Alert, Button, LoadingSpinner } from '../BaseComponents'
import ConfirmationModal from '../BaseComponents/ConfirmationModal'
import { useState } from 'react'

type Props = {
	invitations: Invitation[]
	type?: string
}

function InvitationsTable({ invitations, type }: Props) {
	const dispatch = useAppDispatch()
	const api = useApi();
	const [resendCandidate, setResendCandidate] = useState<Invitation | null>(null)
	const [revokeCandidate, setRevokeCandidate] = useState<Invitation | null>(null)

	const confirmResend = () => {
		if (!resendCandidate) return
		if (type === 'Admin') {
			dispatch(resendInvitation(api, resendCandidate.id))
		} else if (type === 'Company') {
			dispatch(resendInvitationCompany(api, resendCandidate.id))
		}
		setResendCandidate(null)
	}

	const confirmRevoke = () => {
		if (!revokeCandidate) return
		if (type === 'Admin') {
			dispatch(revokeInvitation(api, revokeCandidate.id, 'revoked'))
		} else if (type === 'Company') {
			dispatch(revokeInvitationCompany(api, revokeCandidate.id, 'revoked'))
		}
		setRevokeCandidate(null)
	}

	return (
		<>
		<div className='pt-4'>
			<table className='table-fixed w-full py-2 divide-y-1 divide-charcoal-050'>
				<thead className='border-t-2 border-b-2 border-charcoal-050 bg-charcoal-800'>
					<tr>
						<th scope='col' className='p-lg text-left text-sm text-white leading-5'>
							Organisation Name
						</th>
						<th scope='col' className='p-lg text-left text-sm text-white leading-5 '>
							Sent By
						</th>
						<th scope='col' className='p-lg text-left text-sm text-white leading-5 '>
							Sent To
						</th>
						<th
							scope='col'
							className='p-lg w-28 text-left text-sm text-white leading-5 '
						>
							Status
						</th>
						<th
							scope='col'
							className='p-lg w-36 text-left text-sm text-white leading-5 '
						>
							Actions
						</th>
					</tr>
				</thead>
				<tbody className='divide-y divide-charcoal-050'>
					{invitations.map((invitation: Invitation) => {
						return (
							<tr key={invitation.id}>
								<td className='truncate p-lg text-sm text-white'>{invitation.companyName}</td>
								<td className='truncate p-lg text-sm text-white'>
									{invitation.sentBy === undefined ? '-' : invitation.sentBy}
								</td>
								<td className='truncate p-lg text-sm text-white'>{invitation.sendTo}</td>
								<td className='truncate p-lg text-sm text-white'>{invitation.status}</td>
								<td className='p-lg text-sm text-white'>
									{invitation.updating ? (
										<>
											{/* Loading spinner */}
											<LoadingSpinner inline={true} loadingText='Updating...' small={true} />
										</>
									) : (
										<>
											{invitation.error && (
												<>
													<p className='text-white mb-4'>
														Oops! There was an error updating this invitation:
													</p>
													<p className='text-white text-xs mb-4'>{invitation.error}</p>
												</>
											)}

											{invitation.updated && (
												<>
													<Alert
														title={`Updated invite for ${invitation.companyName}`}
														type='success'
													/>
												</>
											)}

											{invitation.status === 'open' && type !== 'CompanyReceivedInvitations' && (
												<>
													<Button
														requiresWrite
														type='Outlined'
														text='Resend'
														size='sm'
														fullWidth
														additionalClass='mb-2'
														hasLeadingIcon
														isTableRowButton
														icon='fa-solid fa-paper-plane'
														onClick={() => setResendCandidate(invitation)}
													/>
													<Button
														requiresWrite
														type='Primary'
														text='Revoke'
														size='sm'
														fullWidth
														hasLeadingIcon
														isTableRowButton
														icon='fa-solid fa-trash'
														onClick={() => setRevokeCandidate(invitation)}
													/>
												</>
											)}

											{invitation.status === 'open' && type === 'CompanyReceivedInvitations' && (
												<>
													<Button
														requiresWrite
														type='Primary'
														text='Accept'
														size='sm'
														fullWidth
														additionalClass='mb-2'
														hasLeadingIcon
														isTableRowButton
														icon='fa-solid fa-paper-plane'
														onClick={() => {
															// Accept
															dispatch(acceptRejectInvitationCompany(api, invitation.id, true))
														}}
													/>
													<Button
														requiresWrite
														type='Primary'
														text='Reject'
														size='sm'
														fullWidth
														hasLeadingIcon
														isTableRowButton
														icon='fa-solid fa-trash'
														onClick={() => {
															// Reject
															dispatch(acceptRejectInvitationCompany(api, invitation.id, false))
														}}
													/>
												</>
											)}
										</>
									)}
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
			<ConfirmationModal open={!!resendCandidate} onCancel={() => setResendCandidate(null)} onConfirm={confirmResend}>
				<p className='text-white'>
					Are you sure you want to resend the invitation to {resendCandidate?.companyName ?? resendCandidate?.sendTo}?
				</p>
			</ConfirmationModal>
			<ConfirmationModal open={!!revokeCandidate} onCancel={() => setRevokeCandidate(null)} onConfirm={confirmRevoke}>
				<p className='text-white'>
					Are you sure you want to revoke the invitation to {revokeCandidate?.companyName ?? revokeCandidate?.sendTo}?
				</p>
			</ConfirmationModal>
		</>
	)
}
export default InvitationsTable
